import axios from 'axios';
import { Dispatch } from 'redux';
import PAGES_TYPES, { BodyCreateProps, BodyUpdateProps, CommunityPagesByType } from './types';
import { ENV_CONFIG } from 'config/environment';
import { showToast } from 'components/Toast';
import API_ROUTES from 'services/apiRoutes';
import { getAccessToken } from 'services/storage';
import type { RootState } from 'store/create-store';

const headers = {
  'Content-Type': 'application/json',
  'Access-Control-Allow-Origin': '*',
  'X-Auth-Version': 'v2',
};

const isTicketConflict = (category: string, error: any): boolean =>
  category === 'event' &&
  error.response?.status === 409 &&
  JSON.stringify(error.response?.data ?? '')
    .toLowerCase()
    .includes('ticket');

export const getPages =
  (client_id: number | null, category: string, active_role: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch(PAGES_TYPES.pagesStart());
    const clientID = client_id || '';
    //Recommendation pages are donate pages with page_type = REC
    const urlCategory = category === 'event' ? 'event' : 'donate';
    const pageTypes =
      category === 'recommendation'
        ? '&page_types=REC'
        : category === 'donate'
          ? '&page_types=LIST&page_types=COMPACT'
          : '';
    const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.GET_PAGES(clientID, `/${urlCategory}`, pageTypes, active_role)}`;
    try {
      const response = await axios.get(url, {
        headers: { ...headers, Authorization: `Bearer ${getAccessToken()}` },
      });
      dispatch(PAGES_TYPES.pagesSuccess(response.data, category));
    } catch (error: any) {
      showToast(error.message, 'error', error?.response?.status);
      dispatch(PAGES_TYPES.pagesError(error.message));
    }
  };

export const createPage =
  <T extends 'donate' | 'event' | 'recommendation'>(
    body: BodyCreateProps<T>,
    category: T,
    active_role: string
  ) =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch(PAGES_TYPES.pagesStart());
    const urlCategory = category === 'event' ? 'event' : 'donate';
    const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.PAGES(urlCategory, active_role)}`;
    try {
      const response = await axios.post(url, body, {
        headers: { ...headers, Authorization: `Bearer ${getAccessToken()}` },
      });
      dispatch(PAGES_TYPES.pageCreatedSuccess(response.data));
    } catch (error: any) {
      let errorMessage = error.message;
      if (isTicketConflict(category, error)) {
        errorMessage = 'Only 1 ticket can exist per price tier. Delete the existing ticket first';
      }
      showToast(errorMessage, 'error', error?.response?.status);
      dispatch(PAGES_TYPES.pageCreatedError(error.message));
    }
  };

export const updatePage =
  <T extends 'donate' | 'event' | 'recommendation'>(
    body: BodyUpdateProps<T>,
    category: T,
    active_role: string
  ) =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch(PAGES_TYPES.pagesStart());
    const urlCategory = category === 'event' ? 'event' : 'donate';
    const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.PAGES(urlCategory, active_role)}`;
    try {
      const response = await axios.put(url, body, {
        headers: { ...headers, Authorization: `Bearer ${getAccessToken()}` },
      });
      dispatch(PAGES_TYPES.pageCreatedSuccess(response.data));
    } catch (error: any) {
      let errorMessage = error.message;
      if (isTicketConflict(category, error)) {
        errorMessage = 'Only 1 active ticket can exist per price tier.';
      } else if (error.response?.status === 400 && typeof error.response?.data === 'string') {
        errorMessage = error.response?.data;
      }
      showToast(errorMessage, 'error', error?.response?.status);
      dispatch(PAGES_TYPES.pageCreatedError(error.message));
    }
  };

export const getGoogleStorageSignedUrl = async (
  filename: string,
  file_type?: string,
  active_role?: string
): Promise<any> => {
  const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.GOOGLE_STORAGE_URL(filename, file_type, active_role)}`;

  try {
    const response = await axios.get(url, {
      headers: { ...headers, Authorization: `Bearer ${getAccessToken()}` },
    });
    return Promise.resolve(response);
  } catch (error: any) {
    return Promise.reject(error);
  }
};

export const uploadFile =
  (file: File) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<boolean> => {
    const activeRole = getState().admins?.activeRole ?? '';
    try {
      const url = (await getGoogleStorageSignedUrl(file.name, file.type, activeRole)).data;
      await axios.put(url, file, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      return true;
    } catch (error: any) {
      showToast(error.message, 'error', error?.response?.status);
      dispatch(PAGES_TYPES.pagesError(error.message));
      return false;
    }
  };

export const updateImageFilename = async (
  page_type: 'page' | 'event' | 'set',
  page_id_value: string,
  image_type: string,
  filename: string,
  active_role: string
): Promise<void> => {
  const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.IMAGE_FILENAMES(active_role)}`;
  try {
    await axios.post(
      url,
      { page_type, page_id_value, image_type, filename },
      { headers: { ...headers, Authorization: `Bearer ${getAccessToken()}` } }
    );
  } catch (error: any) {
    showToast(error.message, 'error', error?.response?.status);
    throw error;
  }
};

export const pageCleanUp =
  () =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch(PAGES_TYPES.pageCleanUp());
  };

export const getAllPages =
  () =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    dispatch(PAGES_TYPES.pagesStart());
    const activeRole = getState().admins?.activeRole ?? '';
    const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.GET_PAGES('', '', '', activeRole)}`;
    try {
      const response = await axios.get(url, {
        headers: { ...headers, Authorization: `Bearer ${getAccessToken()}` },
      });
      dispatch(PAGES_TYPES.allPagesSuccess(response.data));
    } catch (error: any) {
      showToast(error.message, 'error', error?.response?.status);
      dispatch(PAGES_TYPES.pagesError(error.message));
    }
  };

export const getAllPagesForCommunity =
  (client_id: number | null, active_role: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch(PAGES_TYPES.communityPagesStart());
    const clientID = client_id || '';
    const baseUrl = ENV_CONFIG().API.BASE_URL;
    const authHeaders = { ...headers, Authorization: `Bearer ${getAccessToken()}` };
    try {
      const [donateResult, eventResult, recommendationResult] = await Promise.allSettled([
        axios.get(
          `${baseUrl}${API_ROUTES.GET_PAGES(clientID, '/donate', '&page_types=LIST&page_types=COMPACT', active_role)}`,
          { headers: authHeaders }
        ),
        axios.get(`${baseUrl}${API_ROUTES.GET_PAGES(clientID, '/event', '', active_role)}`, {
          headers: authHeaders,
        }),
        axios.get(
          `${baseUrl}${API_ROUTES.GET_PAGES(clientID, '/donate', '&page_types=REC', active_role)}`,
          { headers: authHeaders }
        ),
      ]);

      const data: CommunityPagesByType = {
        donate: donateResult.status === 'fulfilled' ? (donateResult.value.data?.pages ?? []) : [],
        event: eventResult.status === 'fulfilled' ? (eventResult.value.data?.pages ?? []) : [],
        recommendation:
          recommendationResult.status === 'fulfilled'
            ? (recommendationResult.value.data?.pages ?? [])
            : [],
      };

      const failedResults = [donateResult, eventResult, recommendationResult].filter(
        r => r.status === 'rejected'
      ) as PromiseRejectedResult[];

      if (failedResults.length > 0) {
        const primaryError = failedResults[0].reason as any;
        const message =
          failedResults
            .map(r => (r as any).reason?.message)
            .filter(Boolean)
            .join(' | ') || 'Error loading some pages';
        showToast(message, 'error', primaryError?.response?.status);

        if (failedResults.length === 3) {
          dispatch(PAGES_TYPES.communityPagesError(message));
          return;
        }
      }
      dispatch(PAGES_TYPES.communityPagesSuccess(data));
    } catch (error: any) {
      showToast(error?.message ?? 'Error loading pages', 'error', error?.response?.status);
      dispatch(PAGES_TYPES.communityPagesError(error?.message));
    }
  };

export const getPageDetail =
  (query_str: string, category: string, active_role: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch(PAGES_TYPES.pagesStart());
    const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.GET_PAGE_DETAIL(query_str, category)}`;
    const params = {
      skip_cache: true,
      active_role,
    };
    try {
      const response = await axios.get(url, {
        headers: { ...headers, Authorization: `Bearer ${getAccessToken()}` },
        params,
      });
      dispatch(PAGES_TYPES.pageDetailSuccess(response.data));
    } catch (error: any) {
      showToast(error.message, 'error', error?.response?.status);
      dispatch(PAGES_TYPES.pageDetailError(error.message));
    }
  };
